/**
 * Quick line routing: the utterance goes to the tool model only — no persona,
 * no chat history. The model gets local time + the items currently in view
 * and must answer with structured JSON (ISO datetimes, explicit fields).
 */
import { runToolCompletion } from './api'

export interface ContextItem {
  id: string
  kind: 'task' | 'event' | 'reminder'
  title: string
  when?: string
}

export type QuickIntent =
  | {
      kind: 'create'
      entity: 'task' | 'event' | 'reminder'
      title: string
      datetime: Date | null
      hasTime: boolean
      confidence: number
      missing: string[]
    }
  | { kind: 'complete'; targetId: string; confidence: number }
  | { kind: 'update'; targetId: string; datetime: Date | null; confidence: number }
  | { kind: 'delete'; targetId: string | null }
  | { kind: 'chat' }
  | { kind: 'unknown' }

/** Below this, a tool intent opens a prefilled sheet instead of a confirmation card. */
export const CONFIDENCE_THRESHOLD = 0.75

const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0')

/** Local ISO 8601 with offset, e.g. 2026-09-15T14:05:00+03:00 */
function localIso(d: Date): string {
  const off = -d.getTimezoneOffset()
  const sign = off >= 0 ? '+' : '-'
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(off / 60)}:${pad(off % 60)}`
}

/**
 * Obvious conversational turns (questions, chit-chat) escalate on send without
 * a model round-trip, so navigation never happens while the user is reading.
 */
export function looksConversational(text: string): boolean {
  const t = text.trim().toLowerCase()
  if (t.endsWith('?')) return true
  return /^(mitä|miksi|miten|kuinka|kuka|mikä|missä|milloin|voitko kertoa|kerro|moi|hei|terve|what|why|how|who|tell me)\b/.test(t)
}

function buildSystemPrompt(now: Date, items: ContextItem[]): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' })
  return `You convert one short user utterance (usually Finnish, often colloquial) into a structured action for a personal organiser. You are not a chat assistant. Output ONLY a JSON object, no prose, no code fences.

Current local time: ${localIso(now)} (${weekday}), timezone ${tz}.

Items currently visible on the user's home screen (use these ids to resolve references like "se", "tuo palaveri", "hammaslääkäri"):
${JSON.stringify(items)}

Schema:
{
  "intent": "create" | "complete" | "update" | "delete" | "chat" | "unknown",
  "entity": "task" | "event" | "reminder" | null,
  "title": string | null,        // short item title in the user's language, without the time words
  "datetime": string | null,     // ISO 8601 local time WITH offset, e.g. "2026-09-18T10:00:00+03:00"
  "has_time": boolean,           // false when the user gave only a day
  "target_id": string | null,    // id from the visible items for complete/update/delete
  "confidence": number,          // 0..1, how sure you are of every field
  "missing": string[]            // required fields you could not determine, e.g. ["datetime"]
}

Entities:
- "task": something the user must do ("osta maitoa", "muista maksaa lasku perjantaina").
- "event": a calendar appointment at a time ("palaveri huomenna kymmeneltä", "hammaslääkäri ti 14").
- "reminder": the assistant should call or remind the user at a time ("muistuta mua", "soita mulle huomenna aamulla").

Rules:
- Resolve relative and colloquial times yourself into the datetime field: "huomenna kymmeneltä" = tomorrow 10:00, "puol kymmeneltä" = 09:30, "ens tiistaina aamulla" = next Tuesday 08:00, "illalla" = 18:00, "iltapäivällä" = 14:00.
- events and reminders require a datetime; if you cannot determine it, put "datetime" in missing and lower confidence.
- complete/update/delete require target_id from the visible items; if the reference is ambiguous, set target_id null and confidence below 0.5.
- update currently means moving an item to a new datetime.
- Questions, requests for advice, or anything conversational: intent "chat".
- If the utterance cannot be understood as any action: intent "unknown".`
}

function extractJson(raw: string): Record<string, unknown> | null {
  const cleaned = raw.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '').trim()
  const start = cleaned.indexOf('{')
  const end = cleaned.lastIndexOf('}')
  if (start === -1 || end <= start) return null
  try {
    const parsed = JSON.parse(cleaned.slice(start, end + 1))
    return typeof parsed === 'object' && parsed !== null ? parsed : null
  } catch {
    return null
  }
}

export async function parseQuickLine(text: string, items: ContextItem[], now = new Date()): Promise<QuickIntent> {
  const raw = await runToolCompletion(buildSystemPrompt(now, items), text)
  if (!raw) return { kind: 'unknown' }
  const j = extractJson(raw)
  if (!j) return { kind: 'unknown' }
  console.log('[quickLine] parse', j)

  const confidence = typeof j.confidence === 'number' ? Math.max(0, Math.min(1, j.confidence)) : 0
  const dt = typeof j.datetime === 'string' ? new Date(j.datetime) : null
  const datetime = dt && !isNaN(dt.getTime()) ? dt : null
  const ids = new Set(items.map((i) => i.id))
  const targetId = typeof j.target_id === 'string' && ids.has(j.target_id) ? j.target_id : null
  const missing = Array.isArray(j.missing) ? j.missing.filter((m): m is string => typeof m === 'string') : []

  switch (j.intent) {
    case 'create': {
      const entity = j.entity === 'event' || j.entity === 'reminder' ? j.entity : 'task'
      const title = typeof j.title === 'string' ? j.title.trim() : ''
      if (!title) missing.push('title')
      if (entity !== 'task' && !datetime && !missing.includes('datetime')) missing.push('datetime')
      return { kind: 'create', entity, title, datetime, hasTime: j.has_time === true, confidence, missing }
    }
    case 'complete':
      return targetId ? { kind: 'complete', targetId, confidence } : { kind: 'unknown' }
    case 'update':
      return targetId ? { kind: 'update', targetId, datetime, confidence: datetime ? confidence : 0 } : { kind: 'unknown' }
    case 'delete':
      return { kind: 'delete', targetId }
    case 'chat':
      return { kind: 'chat' }
    default:
      return { kind: 'unknown' }
  }
}
